import { useSelector, useDispatch } from 'react-redux';
import { memo, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useDeviceState, useLeave } from '@/libTTS/useCommon';
import { RootState } from '@/store/storeTTS';
import { setVolume, updateScene, updateAIConfig, updateModelMode, setHistoryMsg, updateLocalUser } from '@/store/storeTTS/slices/room';
import utils from '@/utils/utilsTTS/utils';
import style from './index.module.less';
import CameraOpenSVG from '@/assets/img/CameraOpen.webp';
import CameraCloseSVG from '@/assets/img/CameraClose.webp';
import Mic from '@/assets/img/mic.svg';
import MicOpenSVG from '@/assets/img/MicOpen.webp';
import MicCloseSVG from '@/assets/img/MicClose.webp';
import LeaveRoomSVG from '@/assets/img/LeaveRoomSVG.webp';
import ScreenOnSVG from '@/assets/img/ScreenOn.webp';
import ScreenOffSVG from '@/assets/img/ScreenOff.webp';
import i from '@/assets/img/i.svg';
import AudioController from './AudioController';
import { handleCreateBatchTTS } from '@/api/grt/ai';
import { SCENE, Welcome, Model } from '@/configTTS/common';
import { isVisionMode, AI_MODEL } from '@/configTTS/common';
import Config from '@/configTTS';
import { StreamIndex } from '@volcengine/rtc';
import RtcClient from '@/libTTS/RtcClient';
import { COMMAND } from '@/utils/utilsTTS/handler';
import { v4 as uuidv4 } from 'uuid';
import { number } from 'echarts';

function ToolBar(props: any) {
    const {
        className,
        onBackToChat,
        onDeviceStateChanged,
        ttsMessagesRef,
        ttsContentRef,
        currentAgent,
        originMsgMapRef,
        TTSNewMsg,
        sessionId,
        resetVoiceCallStatus,
        hideTTSActions,
        setHideTTSActions,
        ...rest
    } = props;
    const room = useSelector((state: RootState) => state.room);
    const device = useSelector((state: RootState) => state.device);
    const model = room.aiConfig.Config.LLMConfig?.ModelName;
    const dispatch = useDispatch();
    const leaveRoom = useLeave();
    const animationId = useRef<number>(0);
    const [micPermissionError, setMicPermissionError] = useState(false);
    const [cameraPermissionError, setCameraPermissionError] = useState(false);
    const [showMicError, setShowMicError] = useState(false);
    const [showCameraError, setShowCameraError] = useState(false);
    const permissionCheckInterval = useRef<NodeJS.Timeout | null>(null);
    const micErrorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const cameraErrorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { isAudioPublished, isVideoPublished, isScreenPublished, switchMic, switchCamera, switchScreenCapture } = useDeviceState();
    const prevMicPermissionErrorRef = useRef<boolean>(false);
    const prevCameraPermissionErrorRef = useRef<boolean>(false);
    const [lastOperation, setLastOperation] = useState<'mic' | 'camera' | null>(null);
    // 使用真实的音量数据，设置音量阈值（避免环境噪音触发）
    const rawVolume = room.localUser.audioPropertiesInfo?.linearVolume || 0;
    //音量大小
    let volume = { value: rawVolume > 0.05 ? rawVolume : 0 }; // 过滤掉微小的环境噪音
    // 检查麦克风权限状态
    const checkMicPermission = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setMicPermissionError(true);
                // 确保麦克风状态被正确设置为关闭
                dispatch(
                    updateLocalUser({
                        publishAudio: false
                    })
                );
                return;
            }
            // 尝试获取媒体流以检查权限
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // 如果成功获取到流，关闭它并设置权限状态为正常
            stream.getTracks().forEach((track) => track.stop());
            setMicPermissionError(false);
        } catch (err) {
            // 如果被拒绝或阻止，设置错误状态
            setMicPermissionError(true);
            console.log('麦克风权限被拒绝或阻止:', err);
            // 确保麦克风状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishAudio: false
                })
            );
        }
    };

    // 检查摄像头权限状态
    // const checkCameraPermission = async () => {
    //     try {
    //         if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    //             setCameraPermissionError(true);
    //             // 清理视频播放器
    //             try {
    //                 RtcClient.setLocalVideoPlayer(
    //                     room.localUser.username!,
    //                     undefined,
    //                     false
    //                 );
    //                 // 确保摄像头状态被正确设置为关闭
    //                 dispatch(updateLocalUser({
    //                     publishVideo: false,
    //                 }));
    //             } catch (error) {
    //                 console.error('清理视频播放器时出错:', error);
    //             }
    //             return;
    //         }

    //         // 尝试获取媒体流以检查权限
    //         const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    //         // 如果成功获取到流，关闭它并设置权限状态为正常
    //         stream.getTracks().forEach((track) => track.stop());
    //         setCameraPermissionError(false);
    //     } catch (err) {
    //         // 如果被拒绝或阻止，设置错误状态
    //         setCameraPermissionError(true);
    //         console.log('摄像头权限被拒绝或阻止:', err);
    //         // 清理视频播放器
    //         try {
    //             RtcClient.setLocalVideoPlayer(
    //                 room.localUser.username!,
    //                 undefined,
    //                 false
    //             );
    //             // 确保摄像头状态被正确设置为关闭
    //             dispatch(updateLocalUser({
    //                 publishVideo: false,
    //             }));
    //         } catch (error) {
    //             console.error('清理视频播放器时出错:', error);
    //         }
    //     }
    // };

    // 检查摄像头权限状态
    const checkCameraPermission = async () => {
        try {
            // 使用 permissions API 检查摄像头权限
            //@ts-ignore
            if (navigator.permissions && navigator.permissions.query) {
                //@ts-ignore
                const result = await navigator.permissions.query({ name: 'camera' });
                if (result.state === 'granted') {
                    setCameraPermissionError(false);
                } else if (result.state === 'denied') {
                    setCameraPermissionError(true);
                } else if (result.state === 'prompt') {
                    // 尝试获取媒体流以检查权限
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                        stream.getTracks().forEach((track) => track.stop());
                        setCameraPermissionError(false);
                    } catch (err) {
                        setCameraPermissionError(true);
                    }
                }

                // 监听权限状态变化
                result.addEventListener('change', () => {
                    if (result.state === 'granted') {
                        setCameraPermissionError(false);
                    } else if (result.state === 'denied') {
                        setCameraPermissionError(true);
                    }
                });
            } else {
                //直接尝试获取媒体流
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach((track) => track.stop());
                    setCameraPermissionError(false);
                } catch (err) {
                    setCameraPermissionError(true);
                }
            }
        } catch (err) {
            console.log('摄像头权限检查失败:', err);
            setCameraPermissionError(true);
        }
    };
    useEffect(() => {
        checkMicPermission();
        // 添加对CameraArea发出的摄像头权限错误事件的监听
        const handleCameraPermissionError = (event: CustomEvent) => {
            if (event.detail.error) {
                setCameraPermissionError(true);
                setShowCameraError(true);

                if (cameraErrorTimeoutRef.current) {
                    clearTimeout(cameraErrorTimeoutRef.current);
                }

                cameraErrorTimeoutRef.current = setTimeout(() => {
                    setShowCameraError(false);
                }, 5000);
            }
        };

        window.addEventListener('cameraPermissionError', handleCameraPermissionError as EventListener);

        // 设置定时检查权限状态 (每5秒检查一次)
        permissionCheckInterval.current = setInterval(() => {
            checkMicPermission();
        }, 5000);

        // 监听页面可见性变化，当用户返回页面时重新检查权限
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkMicPermission();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // 清理函数
        return () => {
            if (permissionCheckInterval.current) {
                clearInterval(permissionCheckInterval.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('cameraPermissionError', handleCameraPermissionError as EventListener);
            if (micErrorTimeoutRef.current) {
                clearTimeout(micErrorTimeoutRef.current);
            }
            if (cameraErrorTimeoutRef.current) {
                clearTimeout(cameraErrorTimeoutRef.current);
            }
        };
    }, []);

    // 监听麦克风权限状态变化
    useEffect(() => {
        // 当麦克风权限从正常变为错误时，确保麦克风状态被正确设置为关闭
        if (micPermissionError && !prevMicPermissionErrorRef.current) {
            // 确保麦克风状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishAudio: false
                })
            );
        }
        prevMicPermissionErrorRef.current = micPermissionError;
    }, [micPermissionError]);

    // 监听摄像头权限状态变化
    useEffect(() => {
        // 当摄像头权限从正常变为错误时，确保摄像头状态被正确设置为关闭
        if (cameraPermissionError && !prevCameraPermissionErrorRef.current) {
            // 确保摄像头状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishVideo: false
                })
            );
            // 清理视频播放器
            try {
                RtcClient.setLocalVideoPlayer(room.localUser.username!, undefined, false);
            } catch (error) {
                console.error('清理视频播放器时出错:', error);
            }
        }
        prevCameraPermissionErrorRef.current = cameraPermissionError;
    }, [cameraPermissionError, isVideoPublished, switchCamera]);
    // 处理麦克风开关
    const handleSwitchMic = (enable?: boolean) => {
        const enableMic = enable !== undefined ? enable : !isAudioPublished;
        // 记录最后一次操作为麦克风操作
        setLastOperation('mic');
        // 如果存在麦克风权限错误，则显示提示5秒钟
        if (micPermissionError) {
            setShowMicError(true);

            // 清除之前的计时器
            if (micErrorTimeoutRef.current) {
                clearTimeout(micErrorTimeoutRef.current);
            }

            // 5秒后隐藏提示
            micErrorTimeoutRef.current = setTimeout(() => {
                setShowMicError(false);
            }, 5000);

            // 确保麦克风状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishAudio: false
                })
            );

            return;
        }

        switchMic(enableMic);
    };

    // 处理摄像头开关
    const handleSwitchCamera = async (enable?: boolean) => {
        checkCameraPermission();
        const enableCamera = enable !== undefined ? enable : !isVideoPublished;
        // 记录最后一次操作为摄像头操作
        setLastOperation('camera');
        // 如果存在摄像头权限错误，则阻止操作并提示用户
        if (cameraPermissionError) {
            setShowCameraError(true);
            if (cameraErrorTimeoutRef.current) {
                clearTimeout(cameraErrorTimeoutRef.current);
            }
            cameraErrorTimeoutRef.current = setTimeout(() => {
                setShowCameraError(false);
            }, 5000);

            // 当存在权限错误时，确保视频播放器被清理
            try {
                await RtcClient.stopVideoCapture();
                RtcClient.setLocalVideoPlayer(room.localUser.username!, undefined, false);
            } catch (error) {
                console.error('清理视频播放器时出错:', error);
            }
            // 确保摄像头状态被正确设置为关闭
            dispatch(
                updateLocalUser({
                    publishVideo: false
                })
            );
            return;
        }

        // 在切换前清除历史消息和当前AI状态
        // dispatch(setHistoryMsg([]));
        if (RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }
        if (enableCamera) {
            handleSwitchAIScene(SCENE.TEACHING_ASSISTANT);
            if (isScreenPublished) {
                await switchScreenCapture(false);
            }

            try {
                await switchCamera(true);
                // 只有在不是视觉模式时才切换到视觉模式
                if (Config.Model !== AI_MODEL.VISION) {
                    switchToVisionModel();
                }
                Config.VisionSourceType = StreamIndex.STREAM_INDEX_MAIN;
            } catch (error) {
                // 处理摄像头权限被拒绝的情况
                console.error('摄像头权限被拒绝:', error);
                // 更新摄像头权限状态
                setCameraPermissionError(true);
                // 显示错误提示
                setShowCameraError(true);
                if (cameraErrorTimeoutRef.current) {
                    clearTimeout(cameraErrorTimeoutRef.current);
                }
                cameraErrorTimeoutRef.current = setTimeout(() => {
                    setShowCameraError(false);
                }, 5000);
                // 确保摄像头状态被正确设置为关闭
                dispatch(
                    updateLocalUser({
                        publishVideo: false
                    })
                );
                // 清理视频播放器
                try {
                    RtcClient.setLocalVideoPlayer(room.localUser.username!, undefined, false);
                } catch (playerError) {
                    console.error('清理视频播放器时出错:', playerError);
                }
                return;
            }
        } else {
            try {
                await switchCamera(false);
                if (!isScreenPublished) {
                    handleSwitchAIScene(SCENE.INTELLIGENT_ASSISTANT);
                    switchToNormalModel();
                }
                // 清理视频播放器
                try {
                    RtcClient.setLocalVideoPlayer(room.localUser.username!, undefined, false);
                } catch (playerError) {
                    console.error('清理视频播放器时出错:', playerError);
                }
            } catch (error) {
                console.error('关闭摄像头时出错:', error);
            }
        }

        if (RtcClient.getAudioBotEnabled()) {
            await RtcClient.updateAudioBot();
        }
        if (onDeviceStateChanged) {
            onDeviceStateChanged('camera');
        }
    };

    // 切换AI场景
    const handleSwitchAIScene = (scene: SCENE) => {
        dispatch(updateScene({ scene }));
        // Config.WelcomeSpeech = Welcome[scene];
    };

    // 切换AI模型到视觉模式
    const switchToVisionModel = () => {
        // 在切换模型前，先清除当前正在进行的AI回答
        if (RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }
        Config.Model = AI_MODEL.VISION;
        Config.resetTTSMode();
        dispatch(updateModelMode(Config.ModeSourceType));
        dispatch(updateAIConfig(Config.aigcConfig));
    };
    // 切换AI模型到普通模式
    const switchToNormalModel = () => {
        // 在切换模型前，先保存当前正在进行的AI回答
        if (RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }
        Config.Model = Model[SCENE.INTELLIGENT_ASSISTANT];
        Config.VisionSourceType = StreamIndex.STREAM_INDEX_MAIN;
        dispatch(updateModelMode(Config.ModeSourceType));
        dispatch(updateAIConfig(Config.aigcConfig));
    };

    //控制共享屏幕
    const handleSwitchScreenCapture = async (enable?: boolean) => {
        const enableScreen = enable !== undefined ? enable : !isScreenPublished;
        // 在切换前清除历史消息和当前AI状态
        dispatch(setHistoryMsg([]));
        if (RtcClient.getAudioBotEnabled()) {
            RtcClient.commandAudioBot(COMMAND.INTERRUPT);
        }

        if (enableScreen) {
            handleSwitchAIScene(SCENE.SCREEN_READER);
            if (isVideoPublished) {
                await switchCamera(false);
            }
            switchScreenCapture();
            // 只有在不是视觉模式时才切换到视觉模式
            if (Config.Model !== AI_MODEL.VISION) {
                switchToVisionModel();
            }
            Config.VisionSourceType = StreamIndex.STREAM_INDEX_SCREEN;
        } else {
            switchScreenCapture(false);
            if (!isVideoPublished) {
                handleSwitchAIScene(SCENE.INTELLIGENT_ASSISTANT);
                switchToNormalModel();
            }
        }

        if (RtcClient.getAudioBotEnabled()) {
            await RtcClient.updateAudioBot();
        }
        if (onDeviceStateChanged) {
            onDeviceStateChanged('screen');
        }
    };
    useEffect(() => {
        return () => {
            // 语音通话结束事件
            const voiceCallStatusEvent = new CustomEvent('voiceCallStatusChange', {
                detail: { inVoiceCall: false }
            });
            window.dispatchEvent(voiceCallStatusEvent);
        };
    }, []);
    const handleLeaveRoom = async () => {
        if (setHideTTSActions) {
            console.log('hideTTSActions', hideTTSActions);
            setHideTTSActions(true);
        }
        let ttsMessages = TTSNewMsg.current || [];
        console.log('ttsMessages', ttsMessages);
        if (ttsMessages.length > 0) {
            const filteredMessages = ttsMessages;
            const idPairs = new Map<string, string>();

            filteredMessages.forEach((message: any) => {
                if (message.role === 'local') {
                    if (!idPairs.has(message._id)) {
                        idPairs.set(message._id, uuidv4());
                    }
                }
            });

            filteredMessages.forEach((message: any) => {
                if (message.role === 'assistant' && message.reply_id) {
                    if (idPairs.has(message.reply_id)) {
                        idPairs.set(message._id, idPairs.get(message.reply_id)!);
                    } else if (!idPairs.has(message._id)) {
                        idPairs.set(message._id, uuidv4());
                    }
                }
            });

            const modifiedMessages = filteredMessages.map((message: any) => {
                console.log('message', message);
                let newRole = 'user';
                if (message.role === 'assistant') {
                    newRole = 'assistant';
                } else if (message.role === 'local') {
                    newRole = 'user';
                }

                if (message.is_snapshot === true && message.file_json?.file_url) {
                    const fileJsonObj = Array.isArray(message.file_json) && message.file_json.length > 0 ? message.file_json[0] : message.file_json;

                    if (message.role === 'local') {
                        return {
                            ...message,
                            role: newRole,
                            content_type: 'text',
                            file_json: fileJsonObj,
                            section_id: idPairs.get(message._id)
                        };
                    }

                    return {
                        ...message,
                        role: newRole,
                        content_type: 'text',
                        file_json: fileJsonObj
                    };
                }

                if (message.role === 'local' || (message.role === 'assistant' && idPairs.has(message._id))) {
                    return {
                        ...message,
                        role: newRole,
                        section_id: idPairs.get(message._id)
                    };
                } else if (message.role === 'assistant') {
                    if (message.reply_id && idPairs.has(message.reply_id)) {
                        return {
                            ...message,
                            role: newRole,
                            section_id: idPairs.get(message.reply_id)
                        };
                    } else {
                        const sectionId = uuidv4();
                        return {
                            ...message,
                            role: newRole,
                            section_id: sectionId
                        };
                    }
                }

                return {
                    ...message,
                    role: newRole
                };
            });

            console.log('modifiedMessages========:', modifiedMessages);
            const res = handleCreateBatchTTS({
                message_list: modifiedMessages
            });

            if (res) {
                console.log('语音通话数据保存成功', res);
            } else {
                console.error('语音通话数据保存失败:', res);
            }
        }
        if (isVideoPublished) {
            switchCamera(false);
        }
        if (isScreenPublished) {
            switchScreenCapture(false);
        }
        if (ttsMessagesRef.current) {
            ttsMessagesRef.current = [];
        }
        if (ttsContentRef.current) {
            ttsContentRef.current = [];
        }
        if (TTSNewMsg.current) {
            TTSNewMsg.current = [];
        }
        await leaveRoom();
        dispatch(updateScene({ scene: SCENE.INTELLIGENT_ASSISTANT }));
        dispatch(setHistoryMsg([]));
        //退出后重置场景
        switchToNormalModel();
        resetVoiceCallStatus();
        // 清除所有相关配置
        Config.Model = Model[SCENE.INTELLIGENT_ASSISTANT];
        Config.VisionSourceType = StreamIndex.STREAM_INDEX_MAIN;

        // console.log('hideTTSActions',hideTTSActions);
        // Config.WelcomeSpeech = Welcome[SCENE.INTELLIGENT_ASSISTANT];
        onBackToChat && onBackToChat();
    };

    // 确定麦克风图标状态（考虑权限错误和发布状态）
    const isMicEnabled = isAudioPublished && !micPermissionError;

    // 确定摄像头图标状态（考虑权限错误和发布状态）
    const isCameraEnabled = isVideoPublished && !cameraPermissionError;

    return (
        <div className={`${className} ${style.btns} ${utils.isMobile() ? style.column : ''}`} {...rest}>
            {/* 修改错误提示显示逻辑，根据最后操作类型决定显示哪个错误 */}
            {micPermissionError && showMicError && lastOperation === 'mic' && (
                <div className={style.error}>
                    <img src={i} alt="" style={{ width: '15px', height: '15px', position: 'relative', top: '3px', marginRight: '5px' }} />
                    麦克风权限获取失败，请在浏览器系统设置中打开
                </div>
            )}
            {cameraPermissionError && showCameraError && lastOperation === 'camera' && (
                <div className={style.error}>
                    <img src={i} alt="" style={{ width: '15px', height: '15px', position: 'relative', top: '3px', marginRight: '5px' }} />
                    摄像头权限获取失败，请在浏览器系统设置中打开
                </div>
            )}
            <div className={`${className} ${style.btnswidth} ${utils.isMobile() ? style.column : ''}`}>
                <div className={style.audio}>
                    <AudioController />
                </div>
                <Tooltip title={isCameraEnabled ? '关闭视频' : '打开视频'} placement="top">
                    <img
                        src={isCameraEnabled ? CameraOpenSVG : CameraCloseSVG}
                        onClick={() => handleSwitchCamera()}
                        className={style.btn1}
                        alt="camera"
                        style={{ cursor: 'pointer' }}
                    />
                </Tooltip>
                <Tooltip title={isScreenPublished ? '取消共享屏幕' : '开启屏幕共享'} placement="top">
                    <img
                        src={isScreenPublished ? ScreenOnSVG : ScreenOffSVG}
                        onClick={() => handleSwitchScreenCapture()}
                        className={style.btn2}
                        alt="screenShare"
                        style={{ cursor: 'pointer' }}
                    />
                </Tooltip>
                <Tooltip title={isMicEnabled ? '关闭麦克风' : '打开麦克风'} placement="top">
                    <div
                        style={{
                            width: '30px',
                            height: '30px',
                            position: 'relative',
                            display: 'inline-block',
                            marginRight: '-18px'
                        }}
                    >
                        <div
                            onClick={() => handleSwitchMic(!isAudioPublished)}
                            className={`${style.btn3}`}
                            style={{
                                cursor: 'pointer',
                                width: '29px',
                                height: '28px',
                                lineHeight: '24px',
                                position: 'relative',
                                textAlign: 'center',
                                alignItems: 'center',
                                paddingTop: '2px',
                                zIndex: 1
                            }}
                        >
                            {isMicEnabled ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="icon">
                                        <path
                                            id="Union"
                                            d="M12 1.25C14.347 1.25 16.2506 3.1521 16.251 5.49902V12C16.2508 14.347 14.3471 16.25 12 16.25C9.65311 16.2498 7.75019 14.3469 7.75 12V5.49902C7.75034 3.15225 9.6532 1.25024 12 1.25Z"
                                            fill="#595959"
                                        />
                                        <path
                                            id="Union_2"
                                            d="M19.5 10.8125C19.8796 10.8125 20.1873 11.1205 20.1875 11.5C20.1875 15.7902 16.8875 19.3069 12.6875 19.6562V22C12.6875 22.3797 12.3797 22.6875 12 22.6875C11.6203 22.6875 11.3125 22.3797 11.3125 22V19.6562C7.1125 19.3069 3.8125 15.7902 3.8125 11.5C3.81269 11.1205 4.12042 10.8125 4.5 10.8125C4.87958 10.8125 5.18731 11.1205 5.1875 11.5C5.1875 15.2625 8.23755 18.3125 12 18.3125C15.7625 18.3125 18.8125 15.2625 18.8125 11.5C18.8127 11.1205 19.1204 10.8125 19.5 10.8125Z"
                                            fill="#595959"
                                        />
                                    </g>
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="icon">
                                        <path
                                            id="Subtract"
                                            d="M4.5 10.75C4.91404 10.75 5.24971 11.086 5.25 11.5C5.25024 15.2277 8.27221 18.25 12 18.25C13.4232 18.25 14.7425 17.8077 15.8311 17.0557L16.9053 18.1299C15.7226 19.0059 14.2986 19.5753 12.75 19.7148V22C12.7498 22.414 12.4141 22.75 12 22.75C11.5859 22.75 11.2502 22.414 11.25 22V19.7148C7.04535 19.3359 3.75023 15.8033 3.75 11.5C3.75029 11.086 4.08596 10.75 4.5 10.75ZM14.3301 15.5547C13.661 15.9939 12.8619 16.252 12.002 16.252C9.65559 16.2515 7.75267 14.3483 7.75195 12.002V8.97656L14.3301 15.5547ZM19.5 10.75C19.914 10.75 20.2497 11.086 20.25 11.5C20.2499 13.033 19.8291 14.4667 19.1006 15.6963L17.998 14.5938C18.4772 13.6667 18.7499 12.6155 18.75 11.5C18.7503 11.086 19.086 10.75 19.5 10.75ZM12.002 1.25195C14.349 1.25195 16.2526 3.15401 16.2529 5.50098V12.002C16.2528 12.2665 16.227 12.5252 16.1807 12.7764L7.87695 4.47266C8.33689 2.62268 10.0097 1.25236 12.002 1.25195Z"
                                            fill="#595959"
                                        />
                                        <path
                                            id="Rectangle 346 25722 (Stroke)"
                                            d="M3.95407 2.07846L21.9574 20.0818C22.174 20.2984 22.174 20.6497 21.9574 20.8663C21.7408 21.0829 21.3895 21.0829 21.1729 20.8663L3.16959 2.86295C2.95296 2.64632 2.95296 2.29509 3.16959 2.07846C3.38622 1.86183 3.73744 1.86183 3.95407 2.07846Z"
                                            fill="#FF4D4F"
                                        />
                                    </g>
                                </svg>
                            )}
                            {isMicEnabled && (
                                <div className={style.boxBtn3}>
                                    <div
                                        className={style.valueBtn3}
                                        style={{
                                            transform: `translateY(${16 - (volume.value / 100) * 16}px)`,
                                            height: `${(volume.value / 100) * 30}px`
                                        }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </Tooltip>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            width: '1px',
                            height: '18px',
                            backgroundColor: '#ccc',
                            margin: '0 10px',
                            display: 'inline-block',
                            verticalAlign: 'middle'
                        }}
                    ></div>
                    <img src={LeaveRoomSVG} onClick={() => handleLeaveRoom()} className={style.btn} alt="leave" style={{ cursor: 'pointer' }} />
                </div>
            </div>
        </div>
    );
}

export default memo(ToolBar);
